import {PlcProduitComplementaireDTO} from '../../../../../dtos/plc-produit-complementaire-dto';
import {PeriodeConditionnementDTO} from '../../../../../dtos/produit/declinaison/complementaire/periode-conditionnement-dto';

/**
 * Modifications du paramétrage des produits complémentaires : chaque ligne porte son point de livraison, y compris
 * celles à supprimer — une association ne s'identifie que par le couple (point de livraison, produit décliné).
 */
export class PlcProduitComplementaireChanges {

  produitComplementaireToAddList: PlcProduitComplementaireDTO[] = [];
  produitComplementaireToEditList: PlcProduitComplementaireDTO[] = [];
  produitComplementaireToDeleteList: PlcProduitComplementaireDTO[] = [];

  static buildWithProduitToAddList(produitComplementaireToAddList: PlcProduitComplementaireDTO[]): PlcProduitComplementaireChanges {
    const changes = new PlcProduitComplementaireChanges();
    changes.produitComplementaireToAddList = produitComplementaireToAddList;
    return changes;
  }

  static buildWithEditAndDeleteList(produitComplementaireToEditList: PlcProduitComplementaireDTO[],
                                    produitComplementaireToDeleteList: PlcProduitComplementaireDTO[]): PlcProduitComplementaireChanges {
    const changes = new PlcProduitComplementaireChanges();
    changes.produitComplementaireToEditList = produitComplementaireToEditList;
    changes.produitComplementaireToDeleteList = produitComplementaireToDeleteList;
    return changes;
  }

  /**
   * Clé d'une association : le point de livraison ne suffit pas, le produit décliné non plus. Le couple suffit,
   * d'où le type structurel — une ligne de grille comme un événement de bascule de jour le portent.
   */
  static buildKey(association: { plcId?: number, produitDeclinaisonId?: number }): string {
    return `${association?.plcId}-${association?.produitDeclinaisonId}`;
  }

  static isSame(persisted: PlcProduitComplementaireDTO, current: PlcProduitComplementaireDTO): boolean {
    const persistedJourList = persisted.jourConsommationList ?? [];
    const currentJourList = current.jourConsommationList ?? [];
    return !!persisted.facturable === !!current.facturable
      && !!persisted.commandablePortail === !!current.commandablePortail
      && persistedJourList.length === currentJourList.length
      && persistedJourList.every(jour => currentJourList.includes(jour))
      && PlcProduitComplementaireChanges.isSamePeriodeList(persisted.periodeConditionnementList,
                                                           current.periodeConditionnementList);
  }

  /**
   * `row` ne fournit que le descriptif de la ligne (point de livraison, libellé, unités proposables) : sur les quatre
   * paramètres, c'est la ligne acceptée par le back qui fait foi, sans quoi une valeur vidée par l'utilisateur
   * reviendrait à l'écran.
   *
   * <p>La réponse de sauvegarde n'est pas une relecture de la base : un paramètre absent y vaut donc « non renseigné »,
   * que la base stockera à {@code false} (colonnes {@code NOT NULL DEFAULT 0}) ou en liste vide. D'où la normalisation
   * ici — la même que dans {@link isSame}, pour qu'une valeur absente ne compte pas ensuite comme une modification.</p>
   */
  static mergeParametrage(row: PlcProduitComplementaireDTO,
                          saved: PlcProduitComplementaireDTO): PlcProduitComplementaireDTO {
    return {
      ...row,
      facturable: !!saved.facturable,
      commandablePortail: !!saved.commandablePortail,
      jourConsommationList: saved.jourConsommationList ?? [],
      periodeConditionnementList: saved.periodeConditionnementList ?? []
    };
  }

  private static isSamePeriodeList(persistedList: PeriodeConditionnementDTO[] = [],
                                   currentList: PeriodeConditionnementDTO[] = []): boolean {
    return persistedList.length === currentList.length
      && persistedList.every((periode, index) => PlcProduitComplementaireChanges.isSamePeriode(periode, currentList[index]));
  }

  private static isSamePeriode(persisted: PeriodeConditionnementDTO, current: PeriodeConditionnementDTO): boolean {
    return persisted.dateDebut === current.dateDebut
      && (persisted.uniteDeMesureId ?? null) === (current.uniteDeMesureId ?? null)
      && (persisted.ratio ?? null) === (current.ratio ?? null)
      && (persisted.mcpConditionnementVarianteId ?? null) === (current.mcpConditionnementVarianteId ?? null);
  }

  hasChanges(): boolean {
    return this.produitComplementaireToAddList.length > 0
      || this.produitComplementaireToEditList.length > 0
      || this.produitComplementaireToDeleteList.length > 0;
  }
}
